import type { ContractFieldDefinition, ContractTableDefinition, ContractTableValues, ProjectContract, ProjectContractScope, ProjectContractSignature, UploadedFile } from '~/types/models';
import { stripUndefined } from '~/lib/stripUndefined';
import { missingContractFields, safeHttpsUrl } from '~/lib/contractDocument';

export type ProjectContractDocumentInput = Pick<UploadedFile, 'id' | 'fileName' | 'fileURL' | 'contentType'>;

type BuildProjectContractInput = {
    existingContract?: ProjectContract | null,
    scope?: ProjectContractScope,
    clientId?: string,
    projectId?: string,
    defaultTitle?: string,
    title?: string,
    introMessage?: string,
    bodyText?: string,
    templateId?: string,
    documentHtml?: string,
    fieldDefinitions?: ContractFieldDefinition[],
    fieldValues?: Record<string, string>,
    proposalReferences?: Record<string, string>,
    proposalReferenceLinks?: Record<string, string>,
    tableDefinitions?: ContractTableDefinition[],
    tableValues?: ContractTableValues,
    documentFile?: ProjectContractDocumentInput | null,
    signerId?: string,
    signerName?: string,
    signerEmail?: string,
    signatureText?: string,
    acceptanceConfirmed?: boolean,
    now?: Date,
}

type SignProjectContractInput = {
    contract: ProjectContract,
    signerId?: string,
    signerName?: string,
    signerEmail?: string,
    signatureText?: string,
    acceptanceConfirmed?: boolean,
    now?: Date,
}

function normalizeText(value?: string): string {
    return value?.trim() || '';
}

function normalizeDocument(documentFile?: ProjectContractDocumentInput | null) {
    return documentFile?.fileURL
        ? {
            id: documentFile.id,
            fileName: documentFile.fileName,
            fileURL: documentFile.fileURL,
            contentType: documentFile.contentType,
        }
        : null;
}

export function hasProjectContractContent(contract?: ProjectContract | null): boolean {
    if (!contract) {
        return false;
    }

    return Boolean(normalizeText(contract.bodyText) || normalizeText(contract.documentHtml) || normalizeText(contract.documentFileURL));
}

export function didProjectContractTermsChange(
    existingContract?: ProjectContract | null,
    nextContract?: Pick<ProjectContract, 'title' | 'introMessage' | 'bodyText' | 'documentHtml' | 'fieldDefinitions' | 'fieldValues' | 'proposalReferences' | 'proposalReferenceLinks' | 'tableDefinitions' | 'tableValues' | 'documentFileId' | 'documentFileName' | 'documentFileURL'> | null,
): boolean {
    return normalizeText(existingContract?.title) !== normalizeText(nextContract?.title)
        || normalizeText(existingContract?.introMessage) !== normalizeText(nextContract?.introMessage)
        || normalizeText(existingContract?.bodyText) !== normalizeText(nextContract?.bodyText)
        || normalizeText(existingContract?.documentHtml) !== normalizeText(nextContract?.documentHtml)
        || JSON.stringify(existingContract?.fieldDefinitions || []) !== JSON.stringify(nextContract?.fieldDefinitions || [])
        || JSON.stringify(existingContract?.fieldValues || {}) !== JSON.stringify(nextContract?.fieldValues || {})
        || JSON.stringify(existingContract?.proposalReferences || {}) !== JSON.stringify(nextContract?.proposalReferences || {})
        || JSON.stringify(existingContract?.proposalReferenceLinks || {}) !== JSON.stringify(nextContract?.proposalReferenceLinks || {})
        || JSON.stringify(existingContract?.tableDefinitions || []) !== JSON.stringify(nextContract?.tableDefinitions || [])
        || JSON.stringify(existingContract?.tableValues || {}) !== JSON.stringify(nextContract?.tableValues || {})
        || normalizeText(existingContract?.documentFileId) !== normalizeText(nextContract?.documentFileId)
        || normalizeText(existingContract?.documentFileName) !== normalizeText(nextContract?.documentFileName)
        || normalizeText(existingContract?.documentFileURL) !== normalizeText(nextContract?.documentFileURL);
}

function buildSignature(
    role: ProjectContractSignature['role'],
    type: ProjectContractSignature['type'],
    input: { signerId?: string, signerName?: string, signerEmail?: string, signatureText?: string, acceptanceConfirmed?: boolean, contractVersion: number, now: Date },
): ProjectContractSignature {
    return {
        role,
        type,
        signerId: input.signerId,
        signerName: input.signerName,
        signerEmail: input.signerEmail,
        signatureText: input.signatureText || input.signerName,
        acceptanceConfirmed: input.acceptanceConfirmed,
        signedAt: input.now,
        contractVersion: input.contractVersion,
    };
}

export function buildProjectContract(input: BuildProjectContractInput): ProjectContract | null {
    return publishProjectContract(input);
}

export function buildProjectContractDraft(input: BuildProjectContractInput): ProjectContract | null {
    const now = input.now || new Date();
    const normalizedTitle = normalizeText(input.title);
    const normalizedIntroMessage = normalizeText(input.introMessage);
    const normalizedBodyText = normalizeText(input.bodyText);
    const normalizedDocumentHtml = normalizeText(input.documentHtml);
    const normalizedDocument = normalizeDocument(input.documentFile);
    const rawProposalReferenceLinks = input.proposalReferenceLinks ?? input.existingContract?.proposalReferenceLinks ?? {};
    const normalizedProposalReferenceLinks = Object.fromEntries(Object.entries(rawProposalReferenceLinks).flatMap(([key, value]) => {
        const href = safeHttpsUrl(String(value || ''));
        return /^[a-z][a-z0-9_]{0,63}$/i.test(key) && href ? [[key, href]] : [];
    }));

    if (!normalizedBodyText && !normalizedDocumentHtml && !normalizedDocument?.fileURL) {
        return null;
    }

    const nextContractDraft = {
        title: normalizedTitle || normalizedDocument?.fileName || input.defaultTitle || (input.scope === 'client' ? 'Client agreement' : 'Project contract'),
        introMessage: normalizedIntroMessage || undefined,
        bodyText: normalizedBodyText || undefined,
        documentHtml: normalizedDocumentHtml || undefined,
        fieldDefinitions: normalizedDocumentHtml ? input.fieldDefinitions || [] : undefined,
        fieldValues: normalizedDocumentHtml ? input.fieldValues || {} : undefined,
        proposalReferences: normalizedDocumentHtml ? input.proposalReferences || {} : undefined,
        proposalReferenceLinks: normalizedDocumentHtml ? normalizedProposalReferenceLinks : undefined,
        tableDefinitions: normalizedDocumentHtml ? input.tableDefinitions || [] : undefined,
        tableValues: normalizedDocumentHtml ? input.tableValues || {} : undefined,
        documentFileId: normalizedDocument?.id,
        documentFileName: normalizedDocument?.fileName,
        documentFileURL: normalizedDocument?.fileURL,
    };

    const existingHasContent = hasProjectContractContent(input.existingContract);
    const termsChanged = !existingHasContent || didProjectContractTermsChange(input.existingContract, nextContractDraft);
    const previousVersion = Math.max(1, Number(input.existingContract?.version || 1));
    const version = input.existingContract?.status === 'draft'
        ? previousVersion
        : termsChanged && existingHasContent
            ? previousVersion + 1
            : previousVersion;

    return stripUndefined({
        title: nextContractDraft.title,
        scope: input.scope || input.existingContract?.scope || 'project',
        clientId: input.clientId || input.existingContract?.clientId,
        projectId: input.projectId || input.existingContract?.projectId,
        introMessage: nextContractDraft.introMessage,
        sourceType: normalizedBodyText && normalizedDocument?.fileURL
            ? 'mixed'
            : normalizedDocument?.fileURL
                ? 'file'
                : 'text',
        bodyText: nextContractDraft.bodyText,
        templateId: normalizedDocumentHtml ? input.templateId || input.existingContract?.templateId : undefined,
        documentHtml: nextContractDraft.documentHtml,
        fieldDefinitions: nextContractDraft.fieldDefinitions,
        fieldValues: nextContractDraft.fieldValues,
        proposalReferences: nextContractDraft.proposalReferences,
        proposalReferenceLinks: nextContractDraft.proposalReferenceLinks,
        tableDefinitions: nextContractDraft.tableDefinitions,
        tableValues: nextContractDraft.tableValues,
        documentFileId: nextContractDraft.documentFileId,
        documentFileName: nextContractDraft.documentFileName,
        documentFileURL: nextContractDraft.documentFileURL,
        documentContentType: normalizedDocument?.contentType,
        status: 'draft',
        version,
        publicAccessToken: input.existingContract?.publicAccessToken,
        publicSharedAt: input.existingContract?.publicSharedAt,
        updatedAt: now,
    }) as ProjectContract;
}

export function publishProjectContract(input: BuildProjectContractInput): ProjectContract | null {
    const draftContract = buildProjectContractDraft(input);
    const now = input.now || new Date();

    if (!draftContract) {
        return null;
    }

    if (draftContract.documentHtml) {
        const missing = missingContractFields(draftContract.documentHtml, draftContract.fieldDefinitions || [], draftContract.fieldValues || {});
        if (missing.length) throw new Error(`Fill required contract fields before sending: ${missing.join(', ')}.`);
        if (!/data-contract-signature="tenant"/.test(draftContract.documentHtml) || !/data-contract-signature="client"/.test(draftContract.documentHtml)) {
            throw new Error('Place both the tenant and client signature blocks in the contract before sending.');
        }
    }

    return stripUndefined({
        ...draftContract,
        status: 'awaiting_client',
        tenantSignature: buildSignature('tenant', 'e_signature', {
            signerId: input.signerId,
            signerName: input.signerName,
            signerEmail: input.signerEmail,
            signatureText: input.signatureText || input.signerName,
            acceptanceConfirmed: input.acceptanceConfirmed,
            contractVersion: draftContract.version,
            now,
        }),
        publishedAt: now,
        updatedAt: now,
    }) as ProjectContract;
}

export function signProjectContract(input: SignProjectContractInput): ProjectContract {
    const now = input.now || new Date();
    const version = Math.max(1, Number(input.contract.version || 1));

    return stripUndefined({
        ...input.contract,
        status: 'signed',
        clientSignature: buildSignature('client', 'e_signature', {
            signerId: input.signerId,
            signerName: input.signerName,
            signerEmail: input.signerEmail,
            signatureText: input.signatureText || input.signerName,
            acceptanceConfirmed: input.acceptanceConfirmed,
            contractVersion: version,
            now,
        }),
        updatedAt: now,
    }) as ProjectContract;
}
